import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Table, TableHead, TableRow,
  TableCell, TableBody, Chip, Alert, Tooltip, IconButton,
  Tabs, Tab, Grid, CircularProgress, InputAdornment, TextField
} from '@mui/material';
import {
  ArrowBack, CalculateOutlined, CheckCircle, Send, FileDownload, Search,
  Receipt, Warning, AttachMoney
} from '@mui/icons-material';
import axios from 'axios';
import { fmt, fmtN, sym, locale } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLOR = {
  'Open': 'default', 'Calculated': 'info', 'Pending Approval': 'warning',
  'Approved': 'success', 'Posted': 'primary',
};


const TabPanel = ({ value, index, children }) => value === index ? <Box pt={2}>{children}</Box> : null;

const KpiCard = ({ label, value, color = '#546e7a', sub }) => (
  <Card elevation={2} sx={{ borderLeft: `4px solid ${color}`, borderRadius: 2 }}>
    <CardContent sx={{ py: 1.5 }}>
      <Typography variant="h5" fontWeight={800} color={color} lineHeight={1}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
      {sub && <Typography variant="caption" color="text.disabled" display="block">{sub}</Typography>}
    </CardContent>
  </Card>
);

const PayrollRunCard = () => {
  const navigate = useNavigate();
  const { runNo } = useParams();

  const [run,      setRun]      = useState(null);
  const [entries,  setEntries]  = useState([]);
  const [tab,      setTab]      = useState(0);
  const [search,   setSearch]   = useState('');
  const [loading,  setLoading]  = useState(true);
  const [working,  setWorking]  = useState(false);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, eRes] = await Promise.all([
        axios.get(`${API}/api/payroll/runs/${runNo}`, { headers: hdrs() }),
        axios.get(`${API}/api/payroll/runs/${runNo}/entries`, { headers: hdrs() }),
      ]);
      setRun(rRes.data);
      setEntries(eRes.data || []);
    } catch { setError('Failed to load run'); }
    finally { setLoading(false); }
  }, [runNo]);

  useEffect(() => { load(); }, [load]);

  const calculate = async () => {
    setWorking(true); setError('');
    try {
      const res = await axios.post(`${API}/api/payroll/runs/${runNo}/calculate`, {}, { headers: hdrs() });
      setSuccess(`Calculated ${res.data.entriesCount} employees`); load();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) { setError(err.response?.data?.error || 'Calculation failed'); }
    finally { setWorking(false); }
  };

  const advance = async () => {
    setWorking(true); setError('');
    try {
      await axios.put(`${API}/api/payroll/runs/${runNo}/status`, { action: 'advance' }, { headers: hdrs() });
      setSuccess('Status updated'); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Status change failed'); }
    finally { setWorking(false); }
  };

  const downloadBankFile = async () => {
    try {
      const res = await axios.get(`${API}/api/payroll/runs/${runNo}/bank-file`,
        { headers: hdrs(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = `bank-file-${runNo}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { setError('Download failed'); }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );

  if (!run) return <Alert severity="error">Run not found</Alert>;

  const STATUS_NEXT_ACTION = {
    'Calculated':       { label: 'Submit for Approval', icon: <Send fontSize="small" /> },
    'Pending Approval': { label: 'Approve',              icon: <CheckCircle fontSize="small" /> },
    'Approved':         { label: 'Post',                 icon: <CheckCircle fontSize="small" /> },
  };
  const nextAction = STATUS_NEXT_ACTION[run.status];

  const filteredEntries = entries.filter(e =>
    !search || e.employeeName.toLowerCase().includes(search.toLowerCase()) ||
    e.employeeNo.toLowerCase().includes(search.toLowerCase()) ||
    e.department.toLowerCase().includes(search.toLowerCase())
  );

  // Exceptions
  const exceptions = entries.filter(e =>
    !e.bankAccount || !e.bankName || e.netPay <= 0
  ).map(e => ({
    emp: e,
    issues: [
      ...((!e.bankAccount || !e.bankName) ? ['Missing bank details'] : []),
      ...(e.netPay <= 0 ? ['Zero or negative net pay'] : []),
    ],
  }));

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, mb: 6 }}>

      {/* Header */}
      <Card elevation={3} sx={{ mb: 2, background: 'linear-gradient(135deg, #263238 0%, #37474f 100%)', color: '#fff' }}>
        <CardContent sx={{ py: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Button startIcon={<ArrowBack />} size="small" variant="outlined"
                onClick={() => navigate('/payroll/runs')}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff' } }}>
                Back
              </Button>
              <CalculateOutlined />
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1.2}>{runNo}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {run.periodName} · {run.payGroupCode}
                </Typography>
              </Box>
              <Chip label={run.status} size="small" color={STATUS_COLOR[run.status] || 'default'} />
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              {run.status === 'Open' && (
                <Button variant="contained" startIcon={working ? <CircularProgress size={16} color="inherit" /> : <CalculateOutlined />}
                  onClick={calculate} disabled={working}
                  sx={{ bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}>
                  {working ? 'Calculating…' : 'Calculate'}
                </Button>
              )}
              {run.status === 'Calculated' && (
                <Button variant="contained" startIcon={working ? <CircularProgress size={16} color="inherit" /> : <CalculateOutlined />}
                  onClick={calculate} disabled={working} size="small"
                  sx={{ bgcolor: '#546e7a', '&:hover': { bgcolor: '#37474f' } }}>
                  Recalculate
                </Button>
              )}
              {nextAction && (
                <Button variant="contained" startIcon={working ? <CircularProgress size={16} color="inherit" /> : nextAction.icon}
                  onClick={advance} disabled={working}
                  sx={{ bgcolor: run.status === 'Approved' ? '#2e7d32' : 'rgba(255,255,255,0.15)',
                    '&:hover': { bgcolor: run.status === 'Approved' ? '#1b5e20' : 'rgba(255,255,255,0.25)' } }}>
                  {working ? '…' : nextAction.label}
                </Button>
              )}
              {['Approved', 'Posted'].includes(run.status) && (
                <Button variant="outlined" size="small" startIcon={<FileDownload />}
                  onClick={downloadBankFile}
                  sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}>
                  Bank File
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Card elevation={3}>
        <CardContent>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
            <Tab label="Summary" />
            <Tab label={`Entries (${entries.length})`} />
            <Tab label={
              <Box display="flex" alignItems="center" gap={0.5}>
                Exceptions
                {exceptions.length > 0 && (
                  <Chip label={exceptions.length} size="small" color="error" sx={{ height: 18, fontSize: 11 }} />
                )}
              </Box>
            } />
          </Tabs>

          {/* ── Tab 0: Summary ── */}
          <TabPanel value={tab} index={0}>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={4} md={3}>
                <KpiCard label="Head Count"   value={run.headCount  || 0} color="#546e7a" />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <KpiCard label={`Gross Pay (${sym()})`} value={fmtN(run.totalGross)} color="#1565c0" />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <KpiCard label={`PAYE (${sym()})`}   value={fmtN(run.totalPAYE)}          color="#c62828" />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <KpiCard label={`SHIF (${sym()})`}   value={fmtN(run.totalSHIF)}          color="#e65100" />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <KpiCard label="Housing Levy EE" value={fmtN(run.totalHousingLevyEE)} color="#f57c00" />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <KpiCard label={`NSSF EE (${sym()})`} value={fmtN(run.totalNSSF)}       color="#6a1b9a" />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <KpiCard label="Other Deductions" value={fmtN(run.totalOtherDeductions)} color="#37474f" />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <KpiCard label={`Net Pay (${sym()})`}  value={fmtN(run.totalNetPay)}     color="#2e7d32" />
              </Grid>
            </Grid>

            {/* Employer contributions */}
            <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase" letterSpacing={0.8}>
              Employer Contributions
            </Typography>
            <Box display="flex" gap={3} mt={0.5}>
              <Typography variant="body2">Housing Levy ER: <strong>{sym()} {fmt(run.totalHousingLevyER)}</strong></Typography>
              <Typography variant="body2">NSSF ER: <strong>{sym()} {fmt(run.totalNSSFER)}</strong></Typography>
            </Box>

            {/* Audit trail */}
            {(run.calculatedAt || run.approvedAt || run.postedAt) && (
              <Box mt={2.5} sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase">Audit Trail</Typography>
                <Box display="flex" gap={4} mt={0.5} flexWrap="wrap">
                  {run.calculatedAt  && <Typography variant="caption">Calculated: {new Date(run.calculatedAt).toLocaleString(locale())} by {run.calculatedBy}</Typography>}
                  {run.approvedAt    && <Typography variant="caption">Approved: {new Date(run.approvedAt).toLocaleString(locale())} by {run.approvedBy}</Typography>}
                  {run.postedAt      && <Typography variant="caption">Posted: {new Date(run.postedAt).toLocaleString(locale())} by {run.postedBy}</Typography>}
                </Box>
              </Box>
            )}
          </TabPanel>

          {/* ── Tab 1: Entries ── */}
          <TabPanel value={tab} index={1}>
            <Box mb={1.5}>
              <TextField placeholder="Search employee, department…" size="small" value={search}
                onChange={e => setSearch(e.target.value)} sx={{ minWidth: 260 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Employee</strong></TableCell>
                  <TableCell><strong>Department</strong></TableCell>
                  <TableCell align="right"><strong>Gross</strong></TableCell>
                  <TableCell align="right"><strong>PAYE</strong></TableCell>
                  <TableCell align="right"><strong>SHIF</strong></TableCell>
                  <TableCell align="right"><strong>H.Levy</strong></TableCell>
                  <TableCell align="right"><strong>NSSF</strong></TableCell>
                  <TableCell align="right"><strong>Other</strong></TableCell>
                  <TableCell align="right"><strong>Net Pay</strong></TableCell>
                  <TableCell align="center"><strong>Payslip</strong></TableCell>
                  <TableCell align="center"><strong>Pay Lines</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography variant="body2" color="text.secondary" py={2}>
                        {entries.length === 0 ? 'No entries yet. Click Calculate to generate.' : 'No results match the search.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredEntries.map(e => {
                  const otherDed = e.deductions?.filter(d => !d.isStatutory).reduce((s, d) => s + d.amount, 0) || 0;
                  return (
                    <TableRow key={e.employeeNo} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{e.employeeName}</Typography>
                        <Typography variant="caption" fontFamily="monospace" color="text.secondary">{e.employeeNo}</Typography>
                      </TableCell>
                      <TableCell><Typography variant="caption">{e.department || '—'}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="body2">{fmt(e.grossPay)}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="body2" color="error.main">{fmt(e.paye)}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="body2">{fmt(e.shif)}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="body2">{fmt(e.housingLevyEE)}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="body2">{fmt(e.nssf)}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="body2">{fmt(otherDed)}</Typography></TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} color="success.main">{fmt(e.netPay)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Payslip">
                          <IconButton size="small" color="primary"
                            onClick={() => navigate(`/payroll/runs/${runNo}/payslip/${e.employeeNo}`)}>
                            <Receipt fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Manage Pay Lines">
                          <IconButton size="small" color="secondary"
                            onClick={() => navigate(`/hr/employees/${e.employeeNo}?context=payroll&from=/payroll/runs/${runNo}`)}>
                            <AttachMoney fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TabPanel>

          {/* ── Tab 2: Exceptions ── */}
          <TabPanel value={tab} index={2}>
            {exceptions.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', color: '#2e7d32' }}>
                <CheckCircle sx={{ fontSize: 48, mb: 1, opacity: 0.7 }} />
                <Typography variant="body1" fontWeight={600}>No exceptions found</Typography>
                <Typography variant="body2" color="text.secondary">All entries look good — ready for approval.</Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#ffebee' }}>
                    <TableCell><strong>Employee</strong></TableCell>
                    <TableCell><strong>Issues</strong></TableCell>
                    <TableCell align="right"><strong>Net Pay</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exceptions.map(({ emp, issues }) => (
                    <TableRow key={emp.employeeNo}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{emp.employeeName}</Typography>
                        <Typography variant="caption" color="text.secondary">{emp.employeeNo}</Typography>
                      </TableCell>
                      <TableCell>
                        {issues.map(issue => (
                          <Chip key={issue} label={issue} size="small" color="error" variant="outlined"
                            icon={<Warning fontSize="small" />} sx={{ mr: 0.5, mb: 0.5, fontSize: 11 }} />
                        ))}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700}
                          color={emp.netPay <= 0 ? 'error.main' : 'inherit'}>{fmt(emp.netPay)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabPanel>

        </CardContent>
      </Card>
    </Box>
  );
};

export default PayrollRunCard;
